import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Fremdrift } from '../../api/reise';

interface Props {
  fremdrift: Fremdrift;
  aktivModul: string | null;
  /** Kunden velger selv hvilken modul hen vil svare på (T104). Uten den er listen bare til å se på. */
  onVelg?: (modul: Fremdrift['moduler'][number]) => void;
}

/**
 * Fremdriften: samlet øverst, og så modulene som en rolig trinnliste. Modulen kunden er i, er markert. Med
 * `onVelg` er hver modul en knapp, så kunden kan gå rett til den.
 */
export default function FremdriftListe({ fremdrift, aktivModul, onVelg }: Props) {
  const { t } = useTranslation();
  const samlet = fremdrift.synlige === 0 ? 0 : Math.round((fremdrift.besvart / fremdrift.synlige) * 100);

  return (
    <nav aria-labelledby="fremdrift-tittel" className="kort p-5">
      <div className="mb-4">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h2 id="fremdrift-tittel" className="text-[15px] font-semibold">
            {t('reise.fremdrift.tittel')}
          </h2>
          <span className="text-sm tabular-nums text-slate-500">{samlet} %</span>
        </div>
        {!!fremdrift.minutter && <p className="text-xs text-slate-500">{t('reise.fremdrift.igjen', { minutter: fremdrift.minutter })}</p>}
        {onVelg && <p className="mb-2 mt-1 text-xs text-slate-500">{t('reise.fremdrift.velg')}</p>}
        <div
          role="progressbar"
          aria-label={t('reise.fremdrift.tittel')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={samlet}
          className="h-1.5 overflow-hidden rounded-full bg-slate-100"
        >
          <div className="h-full rounded-full bg-aksent-600 transition-[width] duration-500" style={{ width: `${samlet}%` }} />
        </div>
      </div>

      <ol className="flex flex-col">
        {fremdrift.moduler.map((m) => {
          const prosent = m.synlige === 0 ? 0 : Math.round((m.besvart / m.synlige) * 100);
          const aktiv = m.modulId === aktivModul;
          const ferdig = m.ferdig && m.synlige > 0;
          const innhold = (
            <>
              <span
                aria-hidden="true"
                className={
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ' +
                  (ferdig ? 'border-aksent-600 bg-aksent-600 text-white' : aktiv ? 'border-slate-900 bg-white' : 'border-slate-300 bg-white')
                }
              >
                {ferdig ? <Check className="h-3 w-3" strokeWidth={3} /> : aktiv ? <span className="h-1.5 w-1.5 rounded-full bg-slate-900" /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span className={'block truncate text-sm ' + (aktiv ? 'font-semibold text-slate-900' : ferdig ? 'text-slate-600' : 'text-slate-700')}>
                  {m.tittel}
                </span>
                {aktiv && !ferdig && (
                  <span
                    role="progressbar"
                    aria-label={m.tittel}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={prosent}
                    className="mt-1.5 block h-1 overflow-hidden rounded-full bg-slate-200"
                  >
                    <span className="block h-full rounded-full bg-slate-900" style={{ width: `${prosent}%` }} />
                  </span>
                )}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-slate-500">
                {ferdig ? t('reise.fremdrift.ferdig') : t('reise.fremdrift.modul', { besvart: m.besvart, synlige: m.synlige })}
                {!ferdig && !!m.minutter && (
                  <span className="block text-right text-[11px] text-slate-400">{t('reise.fremdrift.minutter', { minutter: m.minutter })}</span>
                )}
              </span>
            </>
          );
          const klasser = '-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 ' + (aktiv ? 'bg-slate-50' : '');
          return (
            <li key={m.modulId}>
              {onVelg && m.synlige > 0 ? (
                <button
                  type="button"
                  aria-current={aktiv ? 'step' : undefined}
                  onClick={() => onVelg(m)}
                  className={klasser + ' w-[calc(100%+1rem)] text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aksent-600'}
                >
                  {innhold}
                </button>
              ) : (
                <div aria-current={aktiv ? 'step' : undefined} className={klasser}>
                  {innhold}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
